// Sync engine: flushes outbox to Supabase, refreshes pull watermarks.
// Phase 1 ships the flush loop + status events; per-table pull strategies
// register themselves through `registerPuller` from feature modules.
import { supabase } from "@/integrations/supabase/client";
import { offlineDB, type OutboxItem } from "./db";
import { setSyncing, probeOnline } from "./connection";
import { refreshPendingCount } from "./outbox";

type Puller = () => Promise<void>;
const pullers = new Map<string, Puller>();

export function registerPuller(name: string, fn: Puller) {
  pullers.set(name, fn);
}

let _running = false;

export async function syncAll(): Promise<{ pushed: number; failed: number }> {
  if (_running) return { pushed: 0, failed: 0 };
  const db = offlineDB();
  if (!db) return { pushed: 0, failed: 0 };

  const reachable = await probeOnline();
  if (!reachable) return { pushed: 0, failed: 0 };

  _running = true;
  setSyncing(true);

  let pushed = 0;
  let failed = 0;

  try {
    // 1. Flush outbox FIFO.
    const items = await db.outbox.where("status").notEqual("syncing").sortBy("created_at");
    for (const item of items) {
      if (item.id == null) continue;
      await db.outbox.update(item.id, { status: "syncing" });
      try {
        await applyOutboxItem(item);
        await db.outbox.delete(item.id);
        pushed++;
      } catch (err: unknown) {
        failed++;
        const msg = err instanceof Error ? err.message : String(err);
        await db.outbox.update(item.id, {
          status: "failed",
          attempts: (item.attempts ?? 0) + 1,
          last_error: msg.slice(0, 500),
        });
      }
    }

    // 2. Refresh downloaded content packs whose server version changed.
    try {
      const { refreshStalePacks } = await import("./packs");
      await refreshStalePacks();
    } catch (err) { console.warn("[offline] pack refresh failed", err); }

    // 3. Pull registered tables.
    for (const [, fn] of pullers) {
      try { await fn(); } catch (err) { console.warn("[offline] puller failed", err); }
    }

    // 4. Record last-sync timestamp.
    await db.kv.put({ key: "last_sync_at", value: Date.now(), updated_at: Date.now() });
  } finally {
    await refreshPendingCount();
    setSyncing(false);
    _running = false;
  }

  return { pushed, failed };
}

/** Optimistic-concurrency check: refuse to overwrite a newer server row. */
async function guardAllows(item: OutboxItem): Promise<boolean> {
  if (!item.guard) return true;
  const { idColumn, idValue, versionColumn, versionValue } = item.guard;
  const sb = supabase as unknown as {
    from: (t: string) => {
      select: (c: string) => { eq: (k: string, v: unknown) => { maybeSingle: () => Promise<{ data: Record<string, unknown> | null }> } };
    };
  };
  const { data } = await sb.from(item.table).select(versionColumn).eq(idColumn, idValue).maybeSingle();
  const serverVersion = (data?.[versionColumn] as string | undefined) ?? null;
  if (!serverVersion || !versionValue) return true;
  if (new Date(serverVersion).getTime() > new Date(versionValue).getTime()) {
    const db = offlineDB();
    await db?.conflicts.add({
      table: item.table,
      row_id: String(idValue),
      reason: "The server copy changed while you were offline, so your change was not applied.",
      local: item.payload,
      detected_at: Date.now(),
      resolution: "kept_server",
    });
    return false;
  }
  return true;
}

async function applyOutboxItem(item: OutboxItem): Promise<void> {
  if (!(await guardAllows(item))) return;
  // We use a runtime-typed client here on purpose: the offline outbox is
  // table-agnostic, so we bypass the generated literal-union typings.
  const sb = supabase as unknown as {
    rpc: (name: string, args?: Record<string, unknown>) => Promise<{ error: { message: string } | null }>;
    from: (t: string) => {
      insert: (p: unknown) => Promise<{ error: { message: string } | null }>;
      update: (p: unknown) => { eq: (k: string, v: unknown) => unknown };
      delete: () => { eq: (k: string, v: unknown) => unknown };
    };
  };

  if (item.op === "rpc") {
    const { error } = await sb.rpc(item.table, item.payload as Record<string, unknown>);
    if (error) throw new Error(error.message);
    return;
  }
  const q = sb.from(item.table);
  if (item.op === "insert") {
    const { error } = await q.insert(item.payload);
    if (error) throw new Error(error.message);
  } else if (item.op === "update") {
    let upd: unknown = q.update(item.payload);
    if (item.match) {
      for (const [k, v] of Object.entries(item.match)) {
        upd = (upd as { eq: (k: string, v: unknown) => unknown }).eq(k, v);
      }
    }
    const { error } = (await upd) as { error: { message: string } | null };
    if (error) throw new Error(error.message);
  } else if (item.op === "delete") {
    let del: unknown = q.delete();
    if (item.match) {
      for (const [k, v] of Object.entries(item.match)) {
        del = (del as { eq: (k: string, v: unknown) => unknown }).eq(k, v);
      }
    }
    const { error } = (await del) as { error: { message: string } | null };
    if (error) throw new Error(error.message);
  }
}

/** Wire up auto-sync on reconnect + a periodic heartbeat. */
export function startAutoSync() {
  if (typeof window === "undefined") return;
  const tick = () => { void syncAll(); };
  window.addEventListener("makai:reconnect", tick);
  // Heartbeat every 60s while the tab is visible.
  setInterval(() => {
    if (document.visibilityState === "visible" && navigator.onLine) tick();
  }, 60_000);
  // Best-effort initial sync after mount.
  setTimeout(() => { void refreshPendingCount(); }, 250);
  setTimeout(tick, 1500);
}

export async function getLastSyncAt(): Promise<number | null> {
  const db = offlineDB();
  if (!db) return null;
  const row = await db.kv.get("last_sync_at");
  return (row?.value as number) ?? null;
}
